import { CircularProgress, Stack } from '@mui/material'
import ActuatorState from '../components/base/ActuatorState'
import { Actuator } from '../models/smart-home.model'
import React, { useCallback, useMemo } from 'react'
import OnOffToggle from '../components/base/OnOffToggle'
import { useActuatorUpdate } from './use-actuator-update'

interface Props {
  serialNo: number
  actuator: Actuator
  showAutoOptions: boolean
  isLoading: boolean
  selectedState: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedState: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * Hook to handle the common functionalities and content of the header for actuators with auto mode (Timer, Solar Actuator, Battery..).
 */
export const useAutoActuatorHeaderContent = ({
  serialNo,
  actuator,
  showAutoOptions,
  isLoading,
  selectedState,
  setIsLoading,
  setSelectedState,
}: Props): React.ReactNode => {
  const onUpdateActuator = useActuatorUpdate({ setIsLoading })

  const onUpdateSelectedState = useCallback(
    (checked: boolean): void => {
      onUpdateActuator(serialNo, actuator, { command: checked ? 'on' : 'off' })
      setSelectedState(checked)
    },
    [serialNo, actuator, onUpdateActuator, setSelectedState]
  )

  return useMemo(() => {
    if (showAutoOptions) {
      return <ActuatorState state={actuator.state} />
    } else {
      return (
        <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
          {isLoading && <CircularProgress size={18} sx={{ top: '50%', left: '50%' }} />}
          <OnOffToggle
            checked={selectedState}
            onChange={(_, checked) => onUpdateSelectedState(checked)}
            disabled={actuator.state === 'unknown' || isLoading}
          />
        </Stack>
      )
    }
  }, [actuator.state, showAutoOptions, isLoading, selectedState, onUpdateSelectedState])
}
